import type { ImageSizeCategories, DividerType } from 'root/consts';
import {
  Direction,
  AlignmentText,
  ImagePosition,
  Alignment,
  ImageSize,
  HeaderAlignment,
  DirectionText,
  JustifyContentText,
  isSectionNavigationPreset,
} from 'root/consts';
import { getAlignmentByLanguage, getAlignmentText } from 'root/utils/alignment';
import { hasPriceVariantDividers, Preset } from 'root/utils/presets';
import { getIsEndPosition } from './Widget/utils';
import { getDirectionText } from 'root/utils/headerDisplay';
import type { DividerName } from 'root/utils/divider';
import { createPrefixedDividerStyles, getDividerStyles } from 'root/utils/divider';

export const getWidgetPadding = ({
  preset,
  gridMenuVerticalPadding,
  gridMenuHorizontalPadding,
  columnsMenuVerticalPadding,
  columnsMenuHorizontalPadding,
  centerMenuVerticalPadding,
  centerMenuHorizontalPadding,
  sideBySideMenuVerticalPadding,
  sideBySideMenuHorizontalPadding,
  expandStripMenuVerticalPadding,
  expandStripMenuHorizontalPadding,
  expandCenterMenuVerticalPadding,
  expandCenterMenuHorizontalPadding,
}: {
  preset: Preset;
  gridMenuVerticalPadding: number;
  gridMenuHorizontalPadding: number;
  columnsMenuVerticalPadding: number;
  columnsMenuHorizontalPadding: number;
  centerMenuVerticalPadding: number;
  centerMenuHorizontalPadding: number;
  sideBySideMenuVerticalPadding: number;
  sideBySideMenuHorizontalPadding: number;
  expandStripMenuVerticalPadding: number;
  expandStripMenuHorizontalPadding: number;
  expandCenterMenuVerticalPadding: number;
  expandCenterMenuHorizontalPadding: number;
}) => {
  switch (preset) {
    case Preset.Grid:
      return `${gridMenuVerticalPadding}px ${gridMenuHorizontalPadding}px`;
    case Preset.Columns:
      return `${columnsMenuVerticalPadding}px ${columnsMenuHorizontalPadding}px`;
    case Preset.Center:
      return `${centerMenuVerticalPadding}px ${centerMenuHorizontalPadding}px`;
    case Preset.SideBySide:
      return `${sideBySideMenuVerticalPadding}px ${sideBySideMenuHorizontalPadding}px`;
    case Preset.ExpandStrip:
      return `${expandStripMenuVerticalPadding}px ${expandStripMenuHorizontalPadding}px`;
    case Preset.ExpandCenter:
      return `${expandCenterMenuVerticalPadding}px ${expandCenterMenuHorizontalPadding}px`;
  }
};

export const getWidgetOverflow = ({
  preset,
  isMobile,
  hasSectionNavigation,
}: {
  preset: Preset;
  isMobile: boolean;
  hasSectionNavigation?: boolean;
}) => {
  return (isSectionNavigationPreset(preset) && hasSectionNavigation) || (preset === Preset.SideBySide && !isMobile)
    ? 'visible'
    : 'hidden';
};

export const getWidgetDisplay = ({ preset }: { preset: Preset }) => {
  return preset === Preset.SideBySide ? 'grid' : 'block';
};

export const getWidgetGridTemplate = ({
  preset,
  isMobile,
  isRTL,
  imagePosition,
  hasSectionNavigation,
}: {
  preset: Preset;
  isMobile: boolean;
  isRTL: boolean;
  imagePosition: ImagePosition;
  hasSectionNavigation?: boolean;
}) => {
  if (preset !== Preset.SideBySide) {
    return '';
  }

  const isEndPosition = getIsEndPosition(isRTL, imagePosition, ImagePosition.Left);
  if (hasSectionNavigation) {
    if (isMobile) {
      return `"location-navigation"
              "header"
              "image"
              "menu"
              / 1fr`;
    }
    return isEndPosition
      ? `". image" 20px
       "location-navigation image"
       "header image"
       "menu image"
       ". image" auto
       / 1fr 1fr`
      : `"image ." 20px
       "image location-navigation"
       "image header"
       "image menu"
       "image ." auto
       / 1fr 1fr`;
  }
  if (isMobile) {
    return `"header"
            "image"
            "menu"
            / 1fr`;
  }

  return isEndPosition
    ? `". image" 20px
       "header image"
       "menu image"
       ". image" auto
       / 1fr 1fr`
    : `"image ." 20px
       "image header"
       "image menu"
       "image ." auto
       / 1fr 1fr`;
};
export const getUnifiedNavigationGridTemplate = ({ isMobile }: { isMobile: boolean }) => {
  return isMobile
    ? `"dropdown"
    "tabs"`
    : `"dropdown . navigation-divider . tabs"`;
};
export const getUnifiedNavigationGridTemplateColumns = ({
  isMobile,
  preset,
}: {
  isMobile: boolean;
  preset: Preset;
}) => {
  if (!isMobile) {
    return `${preset === Preset.SideBySide ? '200px' : '300px'} 20px 1px 20px 1fr`;
  }

  return `1fr`;
};

export const getIsStretchFullWidth = ({
  preset,
  gridStretchFullWidth,
  centerStretchFullWidth,
  sideBySideStretchFullWidth,
}: {
  preset: Preset;
  gridStretchFullWidth: boolean;
  centerStretchFullWidth: boolean;
  sideBySideStretchFullWidth: boolean;
}) => {
  switch (preset) {
    case Preset.Grid:
      return gridStretchFullWidth;
    case Preset.Center:
      return centerStretchFullWidth;
    case Preset.SideBySide:
      return sideBySideStretchFullWidth;
    default:
      return false;
  }
};

export const getSectionTabsGridTemplateColumns = ({
  isMobile,
  preset,
  gridStretchFullWidth,
  centerStretchFullWidth,
  sideBySideStretchFullWidth,
}: {
  isMobile: boolean;
  preset: Preset;
  gridStretchFullWidth: boolean;
  centerStretchFullWidth: boolean;
  sideBySideStretchFullWidth: boolean;
}) => {
  const isStretchFullWidth = getIsStretchFullWidth({
    preset,
    gridStretchFullWidth,
    centerStretchFullWidth,
    sideBySideStretchFullWidth,
  });

  if (!isMobile && !isStretchFullWidth) {
    return preset === Preset.SideBySide ? `600px` : `800px`;
  }
  return `1fr`;
};

export const getHeaderContainerPadding = ({ preset, isMobile }: { preset: Preset; isMobile: boolean }) => {
  return preset === Preset.SideBySide && !isMobile ? '0px 40px' : '0px';
};
export const getLocationsWithSectionNavigationPadding = ({
  preset,
  isMobile,
  hasSectionNavigation,
}: {
  preset: Preset;
  isMobile: boolean;
  hasSectionNavigation: boolean;
}) => {
  return preset === Preset.SideBySide && !isMobile && hasSectionNavigation ? '0px 40px' : '0px';
};
export const getMenuContentGridArea = ({ preset }: { preset: Preset }) => {
  return preset === Preset.SideBySide ? 'menu' : '';
};

export const getMenuContentPadding = ({ preset, isMobile }: { preset: Preset; isMobile: boolean }) => {
  return preset === Preset.SideBySide && !isMobile ? '0px 40px 20px' : '0px';
};

export const getImageContainerGridArea = ({ preset }: { preset: Preset }) => {
  return preset === Preset.SideBySide ? 'image' : '';
};

export const getImageContainerPosition = ({ preset, isMobile }: { preset: Preset; isMobile: boolean }) => {
  return preset === Preset.SideBySide && !isMobile ? 'sticky' : '';
};

export const getImageContainerTop = ({ preset }: { preset: Preset }) => {
  return preset === Preset.SideBySide ? 0 : '';
};

export const getImageContainerWidth = ({ preset }: { preset: Preset }) => {
  return preset === Preset.SideBySide ? '100%' : '';
};

export const getImageContainerHeight = ({ preset, isMobile }: { preset: Preset; isMobile: boolean }) => {
  return preset === Preset.SideBySide ? (isMobile ? '20vh' : '100vh') : '';
};

export const getImageContainerPaddingBottom = ({ preset, isMobile }: { preset: Preset; isMobile: boolean }) => {
  return preset === Preset.SideBySide && isMobile ? '40px' : '';
};

export const getNavigationMargin = () => {
  return '40px';
};

export const getMenuMaxWidth = ({ preset }: { preset: Preset }) => {
  return preset === Preset.Center ? '800px' : '';
};

export const getMenuMargin = ({ preset }: { preset: Preset }) => {
  return preset === Preset.Center ? '0 auto' : '';
};

export const getSectionsDisplay = ({ preset }: { preset: Preset }) => {
  switch (preset) {
    case Preset.Grid:
    case Preset.Center:
    case Preset.ExpandCenter:
      return 'grid';
    case Preset.Columns:
    case Preset.ExpandStrip:
    case Preset.SideBySide:
      return 'block';
  }
};

export const getSectionsGridTemplateColumns = ({ preset }: { preset: Preset }) => {
  switch (preset) {
    case Preset.Grid:
    case Preset.Center:
    case Preset.ExpandCenter:
      return '1fr';
    case Preset.Columns:
    case Preset.ExpandStrip:
    case Preset.SideBySide:
      return '';
  }
};

export const getSectionsRowGap = ({
  preset,
  gridGap,
  centerGap,
  expandCenterGap,
}: {
  preset: Preset;
  gridGap: number;
  centerGap: number;
  expandCenterGap: number;
}) => {
  switch (preset) {
    case Preset.Grid:
      return `${gridGap}px`;
    case Preset.Center:
      return `${centerGap}px`;
    case Preset.Columns:
    case Preset.SideBySide:
      return '';
    case Preset.ExpandCenter:
      return `${expandCenterGap}px`;
  }
  return '';
};

export const getBottomDividerBetweenSectionsMargin = ({
  preset,
  expandStripBottomDividerMargin,
}: {
  preset: Preset;
  expandStripBottomDividerMargin: number;
}) => {
  return preset === Preset.ExpandStrip ? `${expandStripBottomDividerMargin}px` : '';
};

export const getSectionsMarginTop = ({
  preset,
  showMenuName,
  showMenuDescription,
}: {
  preset: Preset;
  showMenuName: boolean;
  showMenuDescription: boolean;
}) => {
  const noMenuHeader = !showMenuName && !showMenuDescription;

  return preset === Preset.ExpandCenter || noMenuHeader ? '0px' : '40px';
};

export const getSectionsColumns = ({
  preset,
  isMobile,
  columnsNumOfColumns,
}: {
  preset: Preset;
  isMobile: boolean;
  columnsNumOfColumns: number;
}) => {
  if (preset === Preset.Columns) {
    if (isMobile) {
      return 1;
    }
    return columnsNumOfColumns;
  }

  if (preset === Preset.SideBySide) {
    return 1;
  }

  return '';
};

export const getSectionsColumnsGap = ({ preset, columnsGap }: { preset: Preset; columnsGap: number }) => {
  if (preset === Preset.Columns) {
    return `${columnsGap}px`;
  }

  return '';
};

export const getSectionColumnDividerStyle = ({
  preset,
  showColumnDividers,
  columnsNumOfColumns,
}: {
  preset: Preset;
  showColumnDividers: boolean;
  columnsNumOfColumns: number;
}) => {
  if (preset !== Preset.Columns || columnsNumOfColumns <= 1) {
    return 'none';
  }
  return showColumnDividers ? 'solid' : 'none';
};

export const getSectionColumnDividerWidth = ({
  preset,
  sectionColumnDividerWidth,
  columnsNumOfColumns,
}: {
  preset: Preset;
  sectionColumnDividerWidth: number;
  columnsNumOfColumns: number;
}) => {
  if (preset !== Preset.Columns || columnsNumOfColumns <= 1) {
    return '0';
  }
  return sectionColumnDividerWidth;
};

export const getSectionColumnDividerColor = ({
  preset,
  sectionColumnDividerColor,
  columnsNumOfColumns,
}: {
  preset: Preset;
  sectionColumnDividerColor: string;
  columnsNumOfColumns: number;
}) => {
  if (preset !== Preset.Columns || columnsNumOfColumns <= 1) {
    return '';
  }
  return sectionColumnDividerColor;
};

export const getUnifiedNavigationBackgroundPadding = ({
  preset,
  gridPadding,
  centerPadding,
  sideBySidePadding,
  isMobile,
}: {
  preset: Preset;
  gridPadding: number;
  centerPadding: number;
  sideBySidePadding: number;
  isMobile: boolean;
}) => {
  const horizontalPadding = isMobile ? 6 : 8;
  switch (preset) {
    case Preset.Grid:
      return `${horizontalPadding}px ${gridPadding}px`;
    case Preset.Center:
      return `${horizontalPadding}px ${centerPadding}px`;
    case Preset.SideBySide:
      return isMobile
        ? `${horizontalPadding}px ${sideBySidePadding}px`
        : `${horizontalPadding}px ${sideBySidePadding + 40}px`;
    default:
      return '0px';
  }
};
export const getUnifiedNavigationGap = ({ isMobile }: { isMobile: boolean }) => {
  return isMobile ? '6px' : '0px';
};
export const getSectionPadding = ({
  preset,
  gridPadding,
  columnsPadding,
  centerPadding,
  sideBySidePadding,
  expandStripPadding,
  expandCenterPadding,
}: {
  preset: Preset;
  gridPadding: number;
  columnsPadding: number;
  centerPadding: number;
  sideBySidePadding: number;
  expandStripPadding: number[];
  expandCenterPadding: number[];
}) => {
  switch (preset) {
    case Preset.Grid:
      return `${gridPadding}px`;
    case Preset.Columns:
      return `${columnsPadding}px`;
    case Preset.Center:
      return `${centerPadding}px`;
    case Preset.SideBySide:
      return `${sideBySidePadding}px`;
    case Preset.ExpandStrip:
      return `${expandStripPadding[0]}px ${expandStripPadding[1]}px`;
    case Preset.ExpandCenter:
      return `${expandCenterPadding[0]}px ${expandCenterPadding[1]}px`;
  }
};

export const getSectionMarginBottom = ({
  preset,
  columnsMarginBottom,
  sideBySideMarginBottom,
  expandStripMarginBottom,
}: {
  preset: Preset;
  columnsMarginBottom: number;
  sideBySideMarginBottom: number;
  expandStripMarginBottom: number;
}) => {
  switch (preset) {
    case Preset.Columns:
      return `${columnsMarginBottom}px`;
    case Preset.SideBySide:
      return `${sideBySideMarginBottom}px`;
    case Preset.ExpandStrip:
      return `${expandStripMarginBottom}px`;
    default:
      return '';
  }
};

export const getFirstSectionMarginTop = ({ preset }: { preset: Preset }) => {
  return preset === Preset.ExpandCenter ? '40px' : '';
};

export const getSectionTopDividerMarginBottom = ({
  preset,
  sectionTopDividerWidth,
}: {
  preset: Preset;
  sectionTopDividerWidth: number;
}) => {
  if (sectionTopDividerWidth === 0) {
    return '0px';
  }
  return '15px';
};

export const getSectionBottomDividerMarginTop = ({
  sectionBottomDividerWidth,
}: {
  sectionBottomDividerWidth: number;
}) => {
  if (sectionBottomDividerWidth === 0) {
    return '0px';
  }

  return '15px';
};

export const getItemsDisplay = ({ preset }: { preset: Preset }) => {
  switch (preset) {
    case Preset.Grid:
    case Preset.Center:
    case Preset.ExpandCenter:
      return 'grid';
    default:
      return '';
  }
};
export const getItemsGridTemplateColumns = ({
  preset,
  isMobile,
  gridNumOfColumns,
}: {
  preset: Preset;
  isMobile: boolean;
  gridNumOfColumns: number;
}) => {
  switch (preset) {
    case Preset.Grid:
      const gridColumns = isMobile ? Math.min(gridNumOfColumns, 2) : gridNumOfColumns;
      return `repeat(${gridColumns}, 1fr)`;
    case Preset.Center:
    case Preset.ExpandCenter:
      return '1fr';
    default:
      return '';
  }
};

export const getItemsColumnGap = ({ preset, gridItemsColumnGap }: { preset: Preset; gridItemsColumnGap: number }) => {
  if (preset === Preset.Grid) {
    return `${gridItemsColumnGap}px`;
  }

  return '';
};

export const getItemsRowGap = ({
  preset,
  gridItemsRowGap,
  centerItemsRowGap,
  expandCenterItemsRowGap,
}: {
  preset: Preset;
  gridItemsRowGap: number;
  centerItemsRowGap: number;
  expandCenterItemsRowGap: number;
}) => {
  switch (preset) {
    case Preset.Grid:
      return `${gridItemsRowGap}px`;
    case Preset.Center:
      return `${centerItemsRowGap}px`;
    case Preset.ExpandCenter:
      return `${expandCenterItemsRowGap}px`;
    default:
      return '';
  }
};

export const getItemRootMargin = ({
  preset,
  columnsItemsGap,
  sideBySideItemsGap,
  expandStripItemsGap,
}: {
  preset: Preset;
  columnsItemsGap: number;
  sideBySideItemsGap: number;
  expandStripItemsGap: number;
}) => {
  switch (preset) {
    case Preset.Columns:
      return `${columnsItemsGap / 2}px`;
    case Preset.ExpandStrip:
      return `${expandStripItemsGap / 2}px`;
    case Preset.SideBySide:
      return `${sideBySideItemsGap / 2}px`;
    default:
      return '';
  }
};

export const getFirstItemDividerMarginBottom = ({
  showItemDividers,
  itemDividerWidth,
  itemRootMargin,
}: {
  showItemDividers: boolean;
  itemDividerWidth: number;
  itemRootMargin: string;
}) => {
  return !showItemDividers || itemDividerWidth === 0 ? '0' : itemRootMargin;
};

export const getSectionGridTemplate = ({ preset }: { preset: Preset }) => {
  switch (preset) {
    case Preset.ExpandStrip:
    case Preset.ExpandCenter:
      return `[row1-start] "section-name" [row1-end]
              [row2-start] "section-description" [row2-end]
              [row3-start] "section-image" [row3-end]
              [row4-start] "section-items" [row4-end]`;
    case Preset.Columns:
      return `[row1-start] "section-image" [row1-end]
              [row2-start] "section-name" [row2-end]
              [row3-start] "section-description" [row3-end]
              [row4-start] "section-items" [row4-end]`;
    case Preset.Grid:
      return `[row1-start] "section-image" [row1-end]
              [row2-start] "section-top-divider" [row2-end]
              [row3-start] "section-name" [row3-end]
              [row4-start] "section-description" [row4-end]
              [row5-start] "section-bottom-divider" [row5-end]
              [row6-start] "section-items" [row6-end]`;
    case Preset.Center:
      return `[row1-start] "section-image" [row1-end]
              [row2-start] "section-name" [row2-end]
              [row3-start] "section-description" [row3-end]
              [row4-start] "section-items" [row4-end]`;
    case Preset.SideBySide:
      return `[row1-start] "section-name" [row1-end]
              [row2-start] "section-description" [row2-end]
              [row3-start] "section-items" [row3-end]`;
  }
};

export const getSectionImagePaddingBottomExpand = ({ preset }: { preset: Preset }) => {
  return [Preset.ExpandCenter, Preset.ExpandStrip].includes(preset) ? `24px` : 0;
};

export const getShowSectionImageCollapse = ({
  preset,
  showSectionImage,
}: {
  preset: Preset;
  showSectionImage: string;
}) => {
  return [Preset.ExpandCenter, Preset.ExpandStrip].includes(preset) ? 'none' : showSectionImage;
};

type ItemGridTemplateParams = {
  preset: Preset;
  isRTL: boolean;
  gridItemDetailsPadding: number;
  columnsImageWidth: number;
  expandStripImageWidth: number;
  centerItemDetailsPadding: number;
  columnsImagePosition: number;
  expandStripImagePosition: number;
  showItemImage: boolean;
  showDots: boolean;
  isMobile: boolean;
  columnsItemAlignment: number;
  hasPrice?: boolean;
};

export const ITEM_BADGE_AREA = 'badge';

/**
 * The featured badge gets a row of its own directly above the item name. The row mirrors the
 * name row's columns, so the badge lines up with the name and leaves the padding columns - and
 * the image column, which the image spans through - untouched. Injecting it in one place keeps
 * every preset consistent, and the row collapses to nothing for items without a badge.
 */
const withItemBadgeRow = (template: string) => {
  // Optional chaining guards an unknown preset, which produces no template at all.
  const nameRow = template?.match(/"[^"]*\bname\b[^"]*"/);

  if (!nameRow || template.includes(ITEM_BADGE_AREA)) {
    return template;
  }

  const areas = nameRow[0].slice(1, -1).trim().split(/\s+/);
  const isContentArea = (area: string) => area !== '.' && area !== 'image';
  const firstContentArea = areas.findIndex(isContentArea);
  const lastContentArea = areas.reduce((last, area, index) => (isContentArea(area) ? index : last), -1);
  const badgeRowAreas = areas.map((area, index) =>
    index >= firstContentArea && index <= lastContentArea ? ITEM_BADGE_AREA : area
  );

  return template.replace(nameRow[0], `"${badgeRowAreas.join(' ')}" ${nameRow[0]}`);
};

export const getItemGridTemplate = (params: ItemGridTemplateParams) =>
  withItemBadgeRow(getItemGridTemplateAreas(params));

const getItemGridTemplateAreas = ({
  preset,
  isRTL,
  gridItemDetailsPadding,
  columnsImageWidth,
  expandStripImageWidth,
  centerItemDetailsPadding,
  columnsImagePosition,
  expandStripImagePosition,
  showItemImage,
  showDots,
  isMobile,
  columnsItemAlignment,
  hasPrice = true,
}: ItemGridTemplateParams) => {
  switch (preset) {
    case Preset.Grid:
      if (showDots) {
        if (hasPrice) {
          return `[row1-start] "image image image image image" [row1-end]
          [row2-start] ". name dots price ." [row2-end]
          [row3-start] ". description description description ." [row3-end]
          [row4-start] ". labels labels labels ." [row4-end]
          [row5-start] ". . . . ." [row5-end]
          [row6-start] ". modifier-groups modifier-groups modifier-groups ." [row6-end]
          / ${gridItemDetailsPadding}px auto 1fr auto ${gridItemDetailsPadding}px`;
        } else {
          return `[row1-start] "image image image image image" [row1-end]
          [row2-start] ". name name name ." [row2-end]
          [row3-start] ". description description description ." [row3-end]
          [row4-start] ". labels labels labels ." [row4-end]
          [row5-start] ". price-variants price-variants price-variants ." [row5-end]
          [row6-start] ". modifier-groups modifier-groups modifier-groups ." [row6-end]
          / ${gridItemDetailsPadding}px auto 1fr auto ${gridItemDetailsPadding}px`;
        }
      }

      return `[row1-start] "image image image" [row1-end]
      [row2-start] ". name ." [row2-end]
      [row3-start] ". description ." [row3-end]
      [row4-start] ". labels ." [row4-end]
      [row5-start] ". price ." [row5-end]
      [row5-start] ". price-variants ." [row5-end]
      [row6-start] ". modifier-groups ." [row6-end]
      / ${gridItemDetailsPadding}px 1fr ${gridItemDetailsPadding}px`;
    case Preset.Columns:
      const isEndPosition = getIsEndPosition(isRTL, columnsImagePosition, ImagePosition.Right);
      if (!showItemImage) {
        return getItemGridColumnsNoImg(columnsItemAlignment, hasPrice, isMobile);
      }
      return getItemGridColumnsWithImg(columnsItemAlignment, isEndPosition, columnsImageWidth, hasPrice);
    case Preset.Center:
      return ` [row1-start] ". name ." [row1-end]
               [row2-start] ". description ." [row2-end]
               [row3-start] ". labels ." [row3-end]
               [row4-start] ". price ." [row4-end]
               [row4-start] ". price-variants ." [row4-end]
               [row5-start] ". modifier-groups ." [row5-end]
               / ${centerItemDetailsPadding}px 1fr ${centerItemDetailsPadding}px`;
    case Preset.SideBySide:
      return getItemGridSideBySide(hasPrice);
    case Preset.ExpandStrip:
      const isExpandStripImageEndPosition = getIsEndPosition(isRTL, expandStripImagePosition, ImagePosition.Left);
      if (isMobile) {
        if (!showItemImage) {
          return `[row1-start] "." 16px [row1-end]
            [row2-start] "name" [row2-end]
            [row3-start] "description" [row3-end]
            [row4-start] "labels" [row4-end]
            [row5-start] "price-variants" [row5-end]
            [row6-start] "price" [row6-end]
            [row7-start] "modifier-groups" [row7-end]
            /  1fr `;
        }
        return isExpandStripImageEndPosition
          ? `[row1-start] ". . . " 16px [row1-end]
             [row2-start] "name  . image" minmax(0, max-content) [row2-end]
             [row3-start] "description  . image"  minmax(0, max-content) [row3-end]
             [row4-start] "labels . image"  minmax(0, max-content) [row4-end]
             [row5-start] "price-variants . image" [row5-end]
              [row6-start] "price . image"  minmax(0, max-content) [row6-end]
              [row7-start] "modifier-groups . image"  minmax(0, 1fr) [row7-end]
             / 1fr 20px ${expandStripImageWidth}%`
          : `[row1-start] ". . ." 16px [row1-end]
             [row2-start] "image . name" minmax(0, max-content) [row2-end]
             [row3-start] "image . description "  minmax(0, max-content) [row3-end]
             [row4-start] "image . labels"  minmax(0, max-content) [row4-end]
             [row5-start] "image . price-variants"  [row5-end]
             [row6-start] "image . price"  minmax(0, max-content) [row6-end]
             [row7-start] "image . modifier-groups"  minmax(0, 1fr) [row7-end]
             / ${expandStripImageWidth}% 20px 1fr`;
      }

      if (!showItemImage) {
        return `[row1-start] ". . . ." 16px [row1-end]
                  [row2-start] "name dots dots price" [row2-end]
                  [row3-start] "description description . ." [row3-end]
                  [row4-start] "labels labels . ." [row4-end]
                  [row5-start] "price-variants price-variants price-variants price-variants" [row5-end]
                  [row6-start] "modifier-groups modifier-groups modifier-groups modifier-groups" [row6-end]
                  / auto 1fr 25% auto`;
      }
      return isExpandStripImageEndPosition
        ? `[row1-start] ". . . . . ." 16px [row1-end]
             [row2-start] "name dots dots price . image" minmax(0, max-content) [row2-end]
             [row3-start] "description description . . . image"  minmax(0, max-content) [row3-end]
             [row4-start] "labels labels . . . image"  minmax(0, max-content) [row4-end]
             [row5-start] "price-variants price-variants price-variants price-variants . image"  minmax(0, max-content) [row5-end]
             [row6-start] "modifier-groups modifier-groups modifier-groups modifier-groups . image"  minmax(0, 1fr) [row6-end]
             / auto 1fr 20px auto 20px ${expandStripImageWidth}%`
        : `[row1-start] ". . . . . ." 16px [row1-end]
             [row2-start] "image . name dots dots price" minmax(0, max-content) [row2-end]
             [row3-start] "image . description description . ."  minmax(0, max-content) [row3-end]
             [row4-start] "image . labels labels . . "  minmax(0, max-content) [row4-end]
             [row5-start] "image . price-variants price-variants price-variants price-variants"  minmax(0, max-content) [row5-end]
             [row6-start] "image . modifier-groups modifier-groups modifier-groups modifier-groups"  minmax(0, 1fr) [row6-end]
             / ${expandStripImageWidth}% 20px auto 1fr 20px auto`;
    case Preset.ExpandCenter:
      return ` [row1-start] ". name ." [row1-end]
      [row2-start] ". description ." [row2-end]
      [row3-start] ". labels ." [row3-end]
      [row4-start] ". price ." [row4-end]
      [row4-start] ". price-variants ." [row4-end]
      [row5-start] ". modifier-groups ." [row5-end]
      /20px 1fr 20px`;
  }
};

export const getItemGridTemplateNoImage = ({
  preset,
  isRTL,
  gridItemDetailsPadding,
  columnsImageWidth,
  expandStripImageWidth,
  centerItemDetailsPadding,
  columnsImagePosition,
  expandStripImagePosition,
  showDots,
  isMobile,
  columnsItemAlignment,
}: {
  preset: Preset;
  isRTL: boolean;
  gridItemDetailsPadding: number;
  columnsImageWidth: number;
  expandStripImageWidth: number;
  centerItemDetailsPadding: number;
  columnsImagePosition: number;
  expandStripImagePosition: number;
  showDots: boolean;
  isMobile: boolean;
  columnsItemAlignment: number;
}) => {
  return getItemGridTemplate({
    preset,
    isRTL,
    gridItemDetailsPadding,
    columnsImageWidth,
    expandStripImageWidth,
    centerItemDetailsPadding,
    columnsImagePosition,
    expandStripImagePosition,
    showItemImage: false,
    showDots,
    isMobile,
    columnsItemAlignment,
  });
};

export const getItemGridTemplateNoPrice = ({
  preset,
  isRTL,
  gridItemDetailsPadding,
  columnsImageWidth,
  expandStripImageWidth,
  centerItemDetailsPadding,
  columnsImagePosition,
  expandStripImagePosition,
  showItemImage,
  showDots,
  isMobile,
  columnsItemAlignment,
}: {
  preset: Preset;
  isRTL: boolean;
  gridItemDetailsPadding: number;
  columnsImageWidth: number;
  expandStripImageWidth: number;
  centerItemDetailsPadding: number;
  columnsImagePosition: number;
  expandStripImagePosition: number;
  showItemImage: boolean;
  showDots: boolean;
  isMobile: boolean;
  columnsItemAlignment: number;
}) => {
  return getItemGridTemplate({
    preset,
    isRTL,
    gridItemDetailsPadding,
    columnsImageWidth,
    expandStripImageWidth,
    centerItemDetailsPadding,
    columnsImagePosition,
    expandStripImagePosition,
    showItemImage,
    showDots,
    isMobile,
    columnsItemAlignment,
    hasPrice: false,
  });
};

export const getItemGridTemplateNoImageNoPrice = ({
  preset,
  isRTL,
  gridItemDetailsPadding,
  columnsImageWidth,
  expandStripImageWidth,
  centerItemDetailsPadding,
  columnsImagePosition,
  expandStripImagePosition,
  showDots,
  isMobile,
  columnsItemAlignment,
}: {
  preset: Preset;
  isRTL: boolean;
  gridItemDetailsPadding: number;
  columnsImageWidth: number;
  expandStripImageWidth: number;
  centerItemDetailsPadding: number;
  columnsImagePosition: number;
  expandStripImagePosition: number;
  showDots: boolean;
  isMobile: boolean;
  columnsItemAlignment: number;
}) => {
  return getItemGridTemplate({
    preset,
    isRTL,
    gridItemDetailsPadding,
    columnsImageWidth,
    expandStripImageWidth,
    centerItemDetailsPadding,
    columnsImagePosition,
    expandStripImagePosition,
    showItemImage: false,
    showDots,
    isMobile,
    columnsItemAlignment,
    hasPrice: false,
  });
};

export const getItemGridDividerPosition = ({
  itemsColumnGap,
  itemGridDividerWidth,
}: {
  itemsColumnGap: number;
  itemGridDividerWidth: number;
}) => {
  return (itemsColumnGap + itemGridDividerWidth) / -2;
};

export const getItemPriceJustifySelf = ({ preset }: { preset: Preset }) => {
  return [Preset.Columns, Preset.SideBySide, Preset.ExpandStrip].includes(preset) ? 'flex-end' : '';
};

export const getItemPriceWidth = ({ preset }: { preset: Preset }) => {
  return preset === Preset.Grid ? 'fit-content' : '100%';
};
export const getItemPriceMarginBottom = ({ preset }: { preset: Preset }) => {
  return [Preset.Center, Preset.ExpandCenter].includes(preset) ? '0px' : '12px';
};

export const getItemImageAspectRatio = ({
  preset,
  gridItemImageRatio,
  columnsItemImageRatio,
  expandStripItemImageRatio,
}: {
  preset: Preset;
  gridItemImageRatio: number;
  columnsItemImageRatio: number;
  expandStripItemImageRatio: number;
}) => {
  switch (preset) {
    case Preset.Grid:
      return gridItemImageRatio;
    case Preset.Columns:
      return columnsItemImageRatio;
    case Preset.ExpandStrip:
      return expandStripItemImageRatio;
    default:
      return '';
  }
};

export const getItemImageMarginBottom = ({
  preset,
  gridItemImageBottomPadding,
}: {
  preset: Preset;
  gridItemImageBottomPadding: number;
}) => {
  switch (preset) {
    case Preset.Grid:
      return `${gridItemImageBottomPadding}px`;
    case Preset.Columns:
    case Preset.ExpandStrip:
      return '12px';
    default:
      return '';
  }
};

export const getItemPadding = ({
  preset,
  gridItemPadding,
  columnsItemPadding,
  sideBySideItemPadding,
  centerItemPadding,
  expandStripItemPadding,
  expandCenterItemPadding,
}: {
  preset: Preset;
  gridItemPadding: number;
  columnsItemPadding: number;
  sideBySideItemPadding: number;
  centerItemPadding: number;
  expandStripItemPadding: number;
  expandCenterItemPadding: number;
}) => {
  switch (preset) {
    case Preset.Grid:
      return `${gridItemPadding}px`;
    case Preset.Columns:
      return `${columnsItemPadding}px`;
    case Preset.Center:
      return `${centerItemPadding}px`;
    case Preset.SideBySide:
      return `${sideBySideItemPadding}px`;
    case Preset.ExpandStrip:
      return `${expandStripItemPadding}px`;
    case Preset.ExpandCenter:
      return `${expandCenterItemPadding}px`;
  }
};

export const getExtrasDisplay = ({
  preset,
  columnsItemAlignment,
}: {
  preset: Preset;
  columnsItemAlignment: Alignment;
}) => {
  return [Preset.Center, Preset.ExpandCenter].includes(preset) || isColumnsPresetCentered(preset, columnsItemAlignment)
    ? 'flex'
    : 'grid';
};

export const getExtrasJustifyContent = ({
  preset,
  columnsItemAlignment,
}: {
  preset: Preset;
  columnsItemAlignment: Alignment;
}) => {
  return [Preset.Center, Preset.ExpandCenter].includes(preset) || isColumnsPresetCentered(preset, columnsItemAlignment)
    ? 'center'
    : '';
};

export const getExtrasAlignItems = ({
  preset,
  columnsItemAlignment,
}: {
  preset: Preset;
  columnsItemAlignment: Alignment;
}) => {
  return [Preset.Center, Preset.ExpandCenter].includes(preset) || isColumnsPresetCentered(preset, columnsItemAlignment)
    ? 'center'
    : '';
};

export const getExtrasFlexWrap = ({
  preset,
  columnsItemAlignment,
}: {
  preset: Preset;
  columnsItemAlignment: Alignment;
}) => {
  return [Preset.Center, Preset.ExpandCenter].includes(preset) || isColumnsPresetCentered(preset, columnsItemAlignment)
    ? 'wrap'
    : '';
};

export const getExtrasGap = ({
  preset,
  gap = '2px',
  columnsItemAlignment,
}: {
  preset: Preset;
  gap?: string;
  columnsItemAlignment: Alignment;
}) => {
  return [Preset.Center, Preset.ExpandCenter].includes(preset) || isColumnsPresetCentered(preset, columnsItemAlignment)
    ? '0px'
    : gap;
};

export const getVariantDisplay = ({
  preset,
  columnsItemAlignment,
}: {
  preset: Preset;
  columnsItemAlignment: Alignment;
}) => {
  return [Preset.Center, Preset.ExpandCenter].includes(preset) || isColumnsPresetCentered(preset, columnsItemAlignment)
    ? 'grid'
    : '';
};

export const getVariantGridTemplate = ({
  preset,
  columnsItemAlignment,
}: {
  preset: Preset;
  columnsItemAlignment: Alignment;
}) => {
  return [Preset.Center, Preset.ExpandCenter].includes(preset) || isColumnsPresetCentered(preset, columnsItemAlignment)
    ? `[row1-start] ". variant-details . variant-divider" [row1-end]
       / 16px auto 16px auto`
    : '';
};

export const getModifierContainerGridTemplate = ({
  preset,
  columnsItemAlignment,
}: {
  preset: Preset;
  columnsItemAlignment: Alignment;
}) => {
  return [Preset.Center, Preset.ExpandCenter].includes(preset) || isColumnsPresetCentered(preset, columnsItemAlignment)
    ? `[row1-start] ". modifier-details . " [row1-end]
       / 7px auto 7px `
    : '';
};

export const getVariantAlignItems = ({
  preset,
  columnsItemAlignment,
}: {
  preset: Preset;
  columnsItemAlignment: Alignment;
}) => {
  return [Preset.Center, Preset.ExpandCenter].includes(preset) || isColumnsPresetCentered(preset, columnsItemAlignment)
    ? 'center'
    : '';
};

export const getVariantWidth = ({
  preset,
  showDots,
  isMobile,
  columnsItemAlignment,
}: {
  preset: Preset;
  showDots: boolean;
  isMobile: boolean;
  columnsItemAlignment: Alignment;
}) => {
  return preset === Preset.SideBySide ||
    (preset === Preset.Columns && columnsItemAlignment !== Alignment.Center) ||
    (preset === Preset.Grid && showDots) ||
    (preset === Preset.ExpandStrip && !isMobile)
    ? '100%'
    : '';
};

export const getVariantDetailsJustifyContent = ({ preset }: { preset: Preset }) => {
  return [Preset.Columns, Preset.SideBySide, Preset.ExpandStrip].includes(preset) ? 'space-between' : '';
};

export const getVariantDetailsGridArea = ({
  preset,
  columnsItemAlignment,
}: {
  preset: Preset;
  columnsItemAlignment: Alignment;
}) => {
  return [Preset.Center, Preset.ExpandCenter].includes(preset) || isColumnsPresetCentered(preset, columnsItemAlignment)
    ? 'variant-details'
    : '';
};

export const getColumnsImageWidth = ({
  imageWidthEnum,
  isMobile,
}: {
  imageWidthEnum: ImageSizeCategories;
  isMobile: boolean;
}) => {
  const widthMap = isMobile ? ImageSize.MOBILE : ImageSize.DESKTOP;

  return widthMap[imageWidthEnum];
};

export const getMenuTextAlignment = ({
  gridMenuAlignment,
  columnsMenuAlignment,
  sideBySideMenuAlignment,
  expandStripMenuAlignment,
  preset,
}: {
  gridMenuAlignment: Alignment;
  columnsMenuAlignment: Alignment;
  sideBySideMenuAlignment: Alignment;
  expandStripMenuAlignment: Alignment;
  preset: Preset;
}) => {
  const gridAlignmentText = getAlignmentText(gridMenuAlignment);
  const columnsAlignmentText = getAlignmentText(columnsMenuAlignment);
  const sideBySideAlignmentText = getAlignmentText(sideBySideMenuAlignment);
  const expandAlignmentText = getAlignmentText(expandStripMenuAlignment);
  const alignmentMap: Record<Preset, string> = {
    [Preset.Grid]: gridAlignmentText,
    [Preset.Columns]: columnsAlignmentText,
    [Preset.Center]: getAlignmentText(Alignment.Center),
    [Preset.ExpandCenter]: getAlignmentText(Alignment.Center),
    [Preset.SideBySide]: sideBySideAlignmentText,
    [Preset.ExpandStrip]: expandAlignmentText,
  };

  return alignmentMap[preset];
};

export const getSectionTextAlignment = ({
  gridSectionAlignment,
  columnsSectionAlignment,
  sideBySideSectionAlignment,
  expandStripSectionAlignment,
  preset,
}: {
  gridSectionAlignment: Alignment;
  columnsSectionAlignment: Alignment;
  sideBySideSectionAlignment: Alignment;
  expandStripSectionAlignment: Alignment;
  preset: Preset;
}) => {
  const gridAlignmentText = getAlignmentText(gridSectionAlignment);
  const columnsAlignmentText = getAlignmentText(columnsSectionAlignment);
  const sideBySideAlignmentText = getAlignmentText(sideBySideSectionAlignment);
  const expandAlignmentText = getAlignmentText(expandStripSectionAlignment);

  const alignmentMap: Record<Preset, string> = {
    [Preset.Grid]: gridAlignmentText,
    [Preset.Columns]: columnsAlignmentText,
    [Preset.Center]: getAlignmentText(Alignment.Center),
    [Preset.ExpandCenter]: getAlignmentText(Alignment.Center),
    [Preset.SideBySide]: sideBySideAlignmentText,
    [Preset.ExpandStrip]: expandAlignmentText,
  };

  return alignmentMap[preset];
};

export const getSectionImageHeight = ({
  preset,
  gridSectionImageHeight,
  centerSectionImageHeight,
  columnsSectionImageHeight,
  expandCenterSectionImageHeight,
  expandStripSectionImageHeight,
}: {
  preset: Preset;
  gridSectionImageHeight: number;
  centerSectionImageHeight: number;
  columnsSectionImageHeight: number;
  expandCenterSectionImageHeight: number;
  expandStripSectionImageHeight: number;
}) => {
  switch (preset) {
    case Preset.Grid:
      return gridSectionImageHeight;
    case Preset.Columns:
      return columnsSectionImageHeight;
    case Preset.ExpandStrip:
      return expandStripSectionImageHeight;
    case Preset.Center:
      return centerSectionImageHeight;
    case Preset.ExpandCenter:
      return expandCenterSectionImageHeight;
    default:
      return '';
  }
};

export const getSectionImageMarginBottom = ({ isMobile }: { isMobile: boolean }) => {
  return isMobile ? '12px' : '20px';
};

export const getTitleContainerDisplay = ({ preset }: { preset: Preset }) => {
  return [Preset.ExpandCenter, Preset.ExpandStrip].includes(preset) ? 'flex' : '';
};
export const getTitleContainerJustifyContent = ({
  preset,
  sectionTextAlignment,
}: {
  preset: Preset;
  sectionTextAlignment: string;
}) => {
  return preset === Preset.ExpandCenter ||
    (preset === Preset.ExpandStrip && sectionTextAlignment === AlignmentText.Center)
    ? 'center'
    : '';
};
export const getTitleContainerFlexDirection = ({
  preset,
  expandStripSectionAlignment,
}: {
  preset: Preset;
  expandStripSectionAlignment: Alignment;
}) => {
  return preset === Preset.ExpandStrip && expandStripSectionAlignment === Alignment.End ? 'row-reverse' : '';
};
export const getSectionTitleFlexBasis = ({
  preset,
  sectionTextAlignment,
}: {
  preset: Preset;
  sectionTextAlignment: string;
}) => {
  return preset === Preset.ExpandCenter ||
    (preset === Preset.ExpandStrip && sectionTextAlignment === AlignmentText.Center)
    ? 'content'
    : '';
};
export const getTitleContainerPointerEvents = ({ preset }: { preset: Preset }) => {
  return preset === Preset.ExpandCenter || preset === Preset.ExpandStrip ? 'auto' : 'none';
};
export const getItemTextAlignment = ({
  gridItemAlignment,
  columnsItemAlignment,
  preset,
}: {
  gridItemAlignment: Alignment;
  columnsItemAlignment: Alignment;
  preset: Preset;
}) => {
  const alignmentMap: Record<Preset, AlignmentText> = {
    [Preset.Grid]: getAlignmentText(gridItemAlignment),
    [Preset.Columns]: getAlignmentText(columnsItemAlignment),
    [Preset.Center]: getAlignmentText(Alignment.Center),
    [Preset.ExpandCenter]: getAlignmentText(Alignment.Center),
    [Preset.SideBySide]: getAlignmentText(Alignment.Start),
    [Preset.ExpandStrip]: getAlignmentText(Alignment.Start),
  };

  return alignmentMap[preset];
};

export const convertAlignmentToJustifyContent = ({ alignment }: { alignment: AlignmentText }) => {
  switch (alignment) {
    case AlignmentText.Start:
      return 'flex-start';
    case AlignmentText.Center:
      return 'center';
    case AlignmentText.End:
      return 'flex-end';
  }
};

export const getNavigationTextAlignment = ({
  gridMenuNavigationAlignment,
  columnsMenuNavigationAlignment,
  sideBySideMenuNavigationAlignment,
  expandStripMenuNavigationAlignment,
  preset,
  isRTL,
  isRow,
  isMobile,
}: {
  gridMenuNavigationAlignment: Alignment;
  columnsMenuNavigationAlignment: Alignment;
  sideBySideMenuNavigationAlignment: Alignment;
  expandStripMenuNavigationAlignment: Alignment;
  preset: Preset;
  isRTL: boolean;
  isRow?: boolean;
  isMobile?: boolean;
}) => {
  if (isRow && !isMobile) {
    return getAlignmentByLanguage(AlignmentText.End, isRTL);
  }

  const gridAlignmentText = getAlignmentText(gridMenuNavigationAlignment);
  const columnsAlignmentText = getAlignmentText(columnsMenuNavigationAlignment);
  const sideBySideAlignmentText = getAlignmentText(sideBySideMenuNavigationAlignment);
  const expandAlignmentText = getAlignmentText(expandStripMenuNavigationAlignment);
  const alignmentMap: Record<Preset, string> = {
    [Preset.Grid]: getAlignmentByLanguage(gridAlignmentText, isRTL),
    [Preset.Columns]: getAlignmentByLanguage(columnsAlignmentText, isRTL),
    [Preset.Center]: HeaderAlignment.Center,
    [Preset.ExpandCenter]: HeaderAlignment.Center,
    [Preset.SideBySide]: getAlignmentByLanguage(sideBySideAlignmentText, isRTL),
    [Preset.ExpandStrip]: getAlignmentByLanguage(expandAlignmentText, isRTL),
  };

  return alignmentMap[preset];
};

export const showPriceVariantsDivider = ({
  preset,
  showItemVariantsDivider,
}: {
  preset: Preset;
  showItemVariantsDivider: boolean;
}) => {
  return hasPriceVariantDividers(preset) && showItemVariantsDivider ? 'block' : 'none';
};

export const showMenuSideImage = ({
  preset,
  isMobile,
  showMenusSideImage,
}: {
  preset: Preset;
  isMobile: boolean;
  showMenusSideImage: boolean;
}) => {
  return preset === Preset.SideBySide && (!isMobile || showMenusSideImage) ? 'block' : 'none';
};

export const getElementDisplay = ({
  shouldShow,
  displayOption = 'block',
}: {
  shouldShow: boolean;
  displayOption?: string;
}) => {
  return shouldShow ? displayOption : 'none';
};

export const getHeaderAlignItems = ({
  gridHeaderAlignItems,
  columnsHeaderAlignItems,
  sideBySideHeaderAlignItems,
  centerHeaderAlignItems,
  expandStripHeaderAlignItems,
  expandCenterHeaderAlignItems,
  preset,
  isMobile,
}: {
  gridHeaderAlignItems: Alignment;
  columnsHeaderAlignItems: Alignment;
  sideBySideHeaderAlignItems: Alignment;
  centerHeaderAlignItems: Alignment;
  expandStripHeaderAlignItems: Alignment;
  expandCenterHeaderAlignItems: Alignment;
  preset: Preset;
  isMobile: boolean;
}) => {
  if (isMobile) {
    return AlignmentText.Center;
  }

  const alignmentMap: Record<Preset, AlignmentText> = {
    [Preset.Grid]: getAlignmentText(gridHeaderAlignItems),
    [Preset.Columns]: getAlignmentText(columnsHeaderAlignItems),
    [Preset.Center]: getAlignmentText(centerHeaderAlignItems),
    [Preset.ExpandCenter]: getAlignmentText(expandCenterHeaderAlignItems),
    [Preset.SideBySide]: getAlignmentText(sideBySideHeaderAlignItems),
    [Preset.ExpandStrip]: getAlignmentText(expandStripHeaderAlignItems),
  };

  return convertAlignmentToJustifyContent({ alignment: alignmentMap[preset] });
};

export const getUnifiedNavigationTextAlignment = ({
  gridUnifiedNavigationAlignment,
  sideBySideUnifiedNavigationAlignment,
  centerUnifiedNavigationAlignment,
  preset,
  isMobile,
}: {
  gridUnifiedNavigationAlignment: Alignment;
  sideBySideUnifiedNavigationAlignment: Alignment;
  centerUnifiedNavigationAlignment: Alignment;
  preset: Preset;
  isMobile: boolean;
}) => {
  if (isMobile) {
    return AlignmentText.Center;
  }
  switch (preset) {
    case Preset.Grid:
      return getAlignmentText(gridUnifiedNavigationAlignment);
    case Preset.SideBySide:
      return getAlignmentText(sideBySideUnifiedNavigationAlignment);
    case Preset.Center:
      return getAlignmentText(centerUnifiedNavigationAlignment);
    default:
      return AlignmentText.Start;
  }
};

export const getHeaderFlexDirection = ({
  gridHeaderFlexDirection,
  columnsHeaderFlexDirection,
  sideBySideHeaderFlexDirection,
  centerHeaderFlexDirection,
  expandStripHeaderFlexDirection,
  expandCenterHeaderFlexDirection,
  preset,
  isMobile,
}: {
  gridHeaderFlexDirection: Direction;
  columnsHeaderFlexDirection: Direction;
  sideBySideHeaderFlexDirection: Direction;
  centerHeaderFlexDirection: Direction;
  expandStripHeaderFlexDirection: Direction;
  expandCenterHeaderFlexDirection: Direction;
  preset: Preset;
  isMobile: boolean;
}) => {
  if (isMobile) {
    return DirectionText.Column;
  }

  const displayMap: Record<Preset, string> = {
    [Preset.Grid]: getDirectionText(gridHeaderFlexDirection),
    [Preset.Columns]: getDirectionText(columnsHeaderFlexDirection),
    [Preset.Center]: getDirectionText(centerHeaderFlexDirection),
    [Preset.ExpandCenter]: getDirectionText(expandCenterHeaderFlexDirection),
    [Preset.SideBySide]: getDirectionText(sideBySideHeaderFlexDirection),
    [Preset.ExpandStrip]: getDirectionText(expandStripHeaderFlexDirection),
  };

  return displayMap[preset];
};

export const getHeaderContainerGridArea = ({ preset }: { preset: Preset }) => {
  return preset === Preset.SideBySide ? 'header' : '';
};
export const getLocationNavigationGridArea = ({
  preset,
  hasSectionNavigation,
}: {
  preset: Preset;
  hasSectionNavigation: boolean;
}) => {
  return preset === Preset.SideBySide && hasSectionNavigation ? 'location-navigation' : '';
};
export const getDotSvgUrl = ({ size }: { size: number }) => {
  const width = size * 3;
  const cx = width / 2;
  const cy = size / 2;

  return `url("data:image/svg+xml,%3Csvg width='${width}' height='${size}' viewBox='0 0 ${width} ${size}' xmlns='http://www.w3.org/2000/svg'%3E%3Ccircle cx='${cx}' cy='${cy}' r='${cy}' fill='black'/%3E%3C/svg%3E")`;
};

export const getNameAndPriceAlignSelf = ({ showDots }: { showDots: boolean }) => {
  return showDots ? 'end' : '';
};

const fontSizeToNumber = (size?: number | string) => {
  if (size) {
    if (typeof size === 'number') {
      return size;
    }
    const numbers = size.match(/\d+/g);

    if (numbers) {
      return Number(numbers[0]);
    }
  }
};

export const getDotsMargin = ({ nameFont }: { nameFont?: { size?: number | string } }) => {
  const fontSize = fontSizeToNumber(nameFont?.size) ?? 16;

  return `${fontSize / 4}px`;
};

const getJustifyContentForDirection = (direction: Direction): string => {
  return direction === Direction.Horizontal ? JustifyContentText.SpaceBetween : JustifyContentText.None;
};

export const getHeaderJustifyContent = ({
  gridFlexDirection,
  columnsFlexDirection,
  sideBySideFlexDirection,
  centerFlexDirection,
  expandStripFlexDirection,
  expandCenterFlexDirection,
  preset,
  isMobile,
}: {
  gridFlexDirection: Direction;
  columnsFlexDirection: Direction;
  sideBySideFlexDirection: Direction;
  centerFlexDirection: Direction;
  expandStripFlexDirection: Direction;
  expandCenterFlexDirection: Direction;
  preset: Preset;
  isMobile: boolean;
}) => {
  if (isMobile) {
    return getJustifyContentForDirection(Direction.Vertical);
  }

  const directionMap: Record<Preset, Direction> = {
    [Preset.Grid]: gridFlexDirection,
    [Preset.Columns]: columnsFlexDirection,
    [Preset.Center]: centerFlexDirection,
    [Preset.SideBySide]: sideBySideFlexDirection,
    [Preset.ExpandStrip]: expandStripFlexDirection,
    [Preset.ExpandCenter]: expandCenterFlexDirection,
  };

  return getJustifyContentForDirection(directionMap[preset]);
};

export const getRowHeaderDirection = ({
  gridHeaderFlexDirection,
  columnsHeaderFlexDirection,
  sideBySideHeaderFlexDirection,
  centerHeaderFlexDirection,
  expandStripHeaderFlexDirection,
  expandCenterHeaderFlexDirection,
  preset,
  isMobile,
}: {
  gridHeaderFlexDirection: Direction;
  columnsHeaderFlexDirection: Direction;
  sideBySideHeaderFlexDirection: Direction;
  centerHeaderFlexDirection: Direction;
  expandStripHeaderFlexDirection: Direction;
  expandCenterHeaderFlexDirection: Direction;
  preset: Preset;
  isMobile: boolean;
}) => {
  if (isMobile) {
    return Direction.Vertical;
  }

  const directionMap: Record<Preset, Direction> = {
    [Preset.Grid]: gridHeaderFlexDirection,
    [Preset.Columns]: columnsHeaderFlexDirection,
    [Preset.Center]: centerHeaderFlexDirection,
    [Preset.ExpandCenter]: expandCenterHeaderFlexDirection,
    [Preset.SideBySide]: sideBySideHeaderFlexDirection,
    [Preset.ExpandStrip]: expandStripHeaderFlexDirection,
  };
  return directionMap[preset];
};

export const isColumnsPresetCentered = (preset: Preset, itemAlign: Alignment) =>
  preset === Preset.Columns && itemAlign === Alignment.Center;

export const shouldShowItemImage = (showImage: boolean, preset: Preset, itemAlign: Alignment) =>
  !isColumnsPresetCentered(preset, itemAlign) && showImage;

export const shouldShowDotsBorder = (showDots: boolean, showItemPrice: boolean, preset: Preset, itemAlign: Alignment) =>
  !isColumnsPresetCentered(preset, itemAlign) && showDots && showItemPrice;

const getItemGridSideBySide = (hasPrice: boolean = true) => {
  if (!hasPrice) {
    return `[row1-start] "." 16px [row1-end]
            [row2-start] "name" [row2-end]
            [row3-start] "description" [row3-end]
            [row4-start] "labels" [row4-end]
            [row5-start] "price-variants" [row5-end]
            [row6-start] "modifier-groups" [row6-end]
            [row7-start] "." 4px [row7-end]
            / 1fr`;
  }

  return `[row1-start] ". . ." 16px [row1-end]
          [row2-start] "name dots price" [row2-end]
          [row3-start] "description description ." [row3-end]
          [row4-start] "labels labels labels" [row4-end]
          [row5-start] "price-variants price-variants price-variants" [row5-end]
          [row6-start] "modifier-groups modifier-groups modifier-groups" [row6-end]
          [row7-start] ". . ." 4px [row7-end]
          / auto 1fr auto`;
};

const getItemGridColumnsWithImg = (
  itemAlignment: Alignment,
  isImageOnLeft: boolean,
  imageWidth: number,
  hasPrice: boolean = true
) => {
  if (!hasPrice) {
    if (isImageOnLeft) {
      return `[row1-start] ". . ." 16px [row1-end]
              [row2-start] "image . name" minmax(0, max-content) [row2-end]
              [row3-start] "image . description" minmax(0, max-content) [row3-end]
              [row4-start] "image . labels" minmax(0, max-content) [row4-end]
              [row5-start] "image . price-variants" minmax(0, max-content) [row5-end]
              [row6-start] "image . modifier-groups" minmax(0, 1fr) [row6-end]
              / ${imageWidth}% 20px 1fr`;
    } else {
      return `[row1-start] ". . ." 16px [row1-end]
              [row2-start] "name . image" minmax(0, max-content) [row2-end]
              [row3-start] "description . image" minmax(0, max-content) [row3-end]
              [row4-start] "labels . image" minmax(0, max-content) [row4-end]
              [row5-start] "price-variants . image" minmax(0, max-content) [row5-end]
              [row6-start] "modifier-groups . image" minmax(0, 1fr) [row6-end]
              / 1fr 20px ${imageWidth}%`;
    }
  }

  if (isImageOnLeft) {
    switch (itemAlignment) {
      case Alignment.End:
        return `[row1-start] ". . . . ." 16px [row1-end]
              [row2-start] "image . name dots price" minmax(0, max-content) [row2-end]
              [row3-start] "image . description description description" minmax(0, max-content) [row3-end]
              [row4-start] "image . labels labels labels" minmax(0, max-content) [row4-end]
              [row5-start] "image . price-variants price-variants price-variants" minmax(0, max-content) [row5-end]
              [row6-start] "image . modifier-groups modifier-groups modifier-groups" minmax(0, max-content) [row6-end]
              / ${imageWidth}% 20px auto 1fr auto`;
      case Alignment.Start:
      default:
        return `[row1-start] ". . . . . ." 16px [row1-end]
              [row2-start] "image . name dots dots price" minmax(0, max-content) [row2-end]
              [row3-start] "image . description description . ."  minmax(0, max-content) [row3-end]
              [row4-start] "image . labels labels . ."  minmax(0, max-content) [row4-end]
              [row5-start] "image . price-variants price-variants price-variants price-variants" minmax(0, max-content) [row5-end]
              [row6-start] "image . modifier-groups modifier-groups modifier-groups modifier-groups" minmax(0, 1fr) [row6-end]
              / ${imageWidth}% 20px auto 1fr 20px auto`;
    }
  } else {
    switch (itemAlignment) {
      case Alignment.End:
        return `[row1-start] ". . . . ." 16px [row1-end]
              [row2-start] "name dots price . image" minmax(0, max-content) [row2-end]
              [row3-start] "description description description . image" minmax(0, max-content) [row3-end]
              [row4-start] "labels labels labels . image" minmax(0, max-content) [row4-end]
              [row5-start] "price-variants price-variants price-variants . image" minmax(0, max-content) [row5-end]
              [row6-start] "modifier-groups modifier-groups modifier-groups . image" minmax(0, 1fr) [row6-end]
              / auto 1fr auto 20px ${imageWidth}%`;
      case Alignment.Start:
      default:
        return `[row1-start] ". . . . . ." 16px [row1-end]
              [row2-start] "name dots dots price . image" minmax(0, max-content) [row2-end]
              [row3-start] "description description . . . image"  minmax(0, max-content) [row3-end]
              [row4-start] "labels labels . . . image"  minmax(0, max-content) [row4-end]
              [row5-start] "price-variants price-variants price-variants price-variants . image" minmax(0, max-content)[row5-end]
              [row6-start] "modifier-groups modifier-groups modifier-groups modifier-groups . image" minmax(0, 1fr) [row6-end]
              / auto 1fr 20px auto 20px ${imageWidth}%`;
    }
  }
};

const getItemGridColumnsNoImg = (columnsItemAlignment: Alignment, hasPrice: boolean = true, isMobile: boolean) => {
  if (!hasPrice) {
    return `[row1-start] "." 16px [row1-end]
            [row2-start] "name" [row2-end]
            [row3-start] "description" [row3-end]
            [row4-start] "labels" [row4-end]
            [row5-start] "price-variants" [row5-end]
            [row6-start] "modifier-groups" [row6-end]
            / 1fr`;
  }

  if (isMobile) {
    return `[row1-start] "." 16px [row1-end]
            [row2-start] "name" [row2-end]
            [row3-start] "description" [row3-end]
            [row4-start] "labels" [row4-end]
            [row5-start] "price" [row5-end]
            [row6-start] "modifier-groups" [row6-end]
            / 1fr`;
  }

  switch (columnsItemAlignment) {
    case Alignment.End:
      return `[row1-start] ". . ." 16px [row1-end]
              [row2-start] "name dots price" [row2-end]
              [row3-start] "description description description" [row3-end]
              [row4-start] "labels labels labels" [row4-end]
              [row5-start] "price-variants price-variants price-variants" [row5-end]
              [row6-start] "modifier-groups modifier-groups modifier-groups" [row6-end]
              / auto 1fr auto`;
    case Alignment.Center:
      return `[row1-start] ". name . price ." [row1-end]
              [row2-start] "description description description description description" [row2-end]
              [row3-start] "labels labels labels labels labels" [row3-end]
              [row4-start] "price-variants price-variants price-variants price-variants price-variants" [row4-end]
              [row5-start] "modifier-groups modifier-groups modifier-groups modifier-groups modifier-groups" [row5-end]
              / 1fr auto 12px auto 1fr`;
    case Alignment.Start:
    default:
      return `[row1-start] ". . ." 16px [row1-end]
          [row2-start] "name dots price" [row2-end]
          [row3-start] "description description ." [row3-end]
          [row4-start] "labels labels labels" [row4-end]
          [row5-start] "price-variants price-variants price-variants" [row5-end]
          [row6-start] "modifier-groups modifier-groups modifier-groups" [row6-end]
          / auto 1fr auto`;
  }
};

export const getDesignedDividerStyles = ({
  dividerName,
  type,
  color,
  size,
}: {
  dividerName: DividerName;
  type: DividerType;
  color: string;
  size: number;
}) => {
  const dividerStyles = getDividerStyles({
    type,
    color,
    size,
  });

  return createPrefixedDividerStyles({
    dividerName,
    dividerStyles,
  });
};
